// regelfil: src/AGENTS.md
/**
 * Jämförelsestaplar i första skärmen: skolan mot kommunen, kommunen mot riket.
 *
 * Regler (Jimmy, 19 okt 2026):
 * - Stapellängd betyder aldrig bra eller dåligt. Ämnet ritas i primärgrönt,
 *   jämförelsen i dämpad ton. Ingen färg, pil, bock eller symbol bär omdöme;
 *   riktningen skrivs ut i texten under.
 * - Jämförelse mot kommunen visas bara när kommunen har minst fem skolor med
 *   samma mått och skolform (samma golv som kommunsnitten). Under golvet visas
 *   skolans eget tal utan stapel.
 * - Talen står som text, så att en skärmläsare läser dem utan att se staplarna.
 */
import { SKOLVERKET_KOMMUN_SCOPE } from "./kommun-snitt";
import type { BenchmarkRow } from "./school-key-figures";
import type { SchoolStatistic } from "./school-statistics";

export const JAMFOR_GOLV = 5;

export type StapelMatt = {
  skolform: "forskola" | "gr" | "gy";
  metric: string;
  /** "4,3 barn per årsarbetare" — enhet efter talet. */
  enhet: string;
  /** Rubrik när talet står först: "Barn per årsarbetare". */
  rubrik: string;
  decimaler: number;
  riktning: string;
  kalla: string;
};

export const STAPELMATT: Record<StapelMatt["skolform"], StapelMatt> = {
  forskola: {
    skolform: "forskola",
    metric: "childrenPerStaffPreschool",
    enhet: "barn per årsarbetare",
    rubrik: "Barn per årsarbetare",
    decimaler: 1,
    riktning: "Färre barn per årsarbetare betyder fler vuxna per barn.",
    kalla: "Kolada",
  },
  gr: {
    skolform: "gr",
    metric: "averageGradesMeritRating9thGrade",
    enhet: "i meritvärde",
    rubrik: "Meritvärde i årskurs 9",
    decimaler: 1,
    riktning: "Högre meritvärde betyder högre betyg i årskurs 9.",
    kalla: "Skolverket",
  },
  gy: {
    skolform: "gy",
    metric: "gradePointsAverage",
    enhet: "i genomsnittlig betygspoäng",
    rubrik: "Genomsnittlig betygspoäng",
    decimaler: 1,
    riktning: "Högre betygspoäng betyder högre betyg vid gymnasieexamen.",
    kalla: "Kolada",
  },
};

export type Jamforelse = {
  matt: StapelMatt;
  period: string;
  amne: { etikett: string; varde: number };
  /** Saknas under golvet — då visas bara ämnets tal. */
  jamfor: { etikett: string; varde: number; antal: number } | null;
};

export function formateraTal(v: number, decimaler: number): string {
  return v.toLocaleString("sv-SE", {
    minimumFractionDigits: decimaler,
    maximumFractionDigits: decimaler,
  });
}

/** "4,3 barn per årsarbetare, kommunen 4,9" — läsbart utan staplarna. */
export function jamforText(j: Jamforelse): string {
  const t = (v: number) => formateraTal(v, j.matt.decimaler);
  const egen = `${t(j.amne.varde)} ${j.matt.enhet}`;
  return j.jamfor ? `${egen}, ${j.jamfor.etikett.toLocaleLowerCase("sv")} ${t(j.jamfor.varde)}` : egen;
}

/** Stapelbredder i procent av den längsta; samma skala för båda. */
export function stapelBredd(a: number, b: number): [number, number] {
  const max = Math.max(a, b);
  if (!(max > 0)) return [0, 0];
  return [Math.round((a / max) * 100), Math.round((b / max) * 100)];
}

function senaste(rows: SchoolStatistic[], m: StapelMatt) {
  return rows
    .filter((r) => r.skolform === m.skolform && r.metric === m.metric && r.value_num != null && Number.isFinite(Number(r.value_num)))
    .sort((a, b) => b.time_period.localeCompare(a.time_period))[0];
}

/**
 * Skolan mot kommunsnittet. Första mått i ordningen där skolan har ett tal.
 * Kommunsnittet läses för samma läsår; under golvet blir jamfor null.
 */
export function skolansJamforelse(
  rows: SchoolStatistic[],
  benchmarks: BenchmarkRow[],
  kommunkod: string | null,
  ordning: StapelMatt["skolform"][] = ["gr", "forskola", "gy"],
): Jamforelse | null {
  for (const sf of ordning) {
    const m = STAPELMATT[sf];
    const egen = senaste(rows, m);
    if (!egen) continue;
    const b = kommunkod
      ? benchmarks.find(
          (x) =>
            x.scope === "kommunkod" &&
            x.scope_key === kommunkod &&
            x.skolform === m.skolform &&
            x.metric === m.metric &&
            x.time_period === egen.time_period,
        )
      : undefined;
    const ok = b && Number(b.unit_count) >= JAMFOR_GOLV && b.avg_value != null;
    return {
      matt: m,
      period: egen.time_period,
      amne: { etikett: "Skolan", varde: Number(egen.value_num) },
      jamfor: ok ? { etikett: "Kommunen", varde: Number(b!.avg_value), antal: Number(b!.unit_count) } : null,
    };
  }
  return null;
}

/**
 * Kommunen mot riket. För meritvärde läses Skolverkets egna kommunsnitt
 * (scope "kommun_skolverket") först — de täcker alla kommuner utan vårt
 * femskolorsgolv, och både kommunen och riket kommer från samma källa och
 * årgång. Saknas de faller vi tillbaka på vårt beräknade snitt, som bara
 * finns över golvet (snittet byggs inte under fem skolor).
 */
export function kommunensJamforelse(
  benchmarks: BenchmarkRow[],
  kommunkod: string,
  ordning: StapelMatt["skolform"][],
): Jamforelse | null {
  for (const sf of ordning) {
    const m = STAPELMATT[sf];
    if (sf === "gr") {
      const skolverkets = benchmarks
        .filter(
          (x) =>
            x.scope === SKOLVERKET_KOMMUN_SCOPE &&
            x.scope_key === kommunkod &&
            x.skolform === m.skolform &&
            x.metric === m.metric &&
            x.avg_value != null,
        )
        .sort((a, b) => b.time_period.localeCompare(a.time_period));
      for (const k of skolverkets) {
        const riket = benchmarks.find(
          (x) =>
            x.scope === SKOLVERKET_KOMMUN_SCOPE &&
            x.scope_key === "se" &&
            x.skolform === m.skolform &&
            x.metric === m.metric &&
            x.time_period === k.time_period &&
            x.avg_value != null,
        );
        if (!riket) continue;
        return {
          matt: m,
          period: k.time_period,
          amne: { etikett: "Kommunen", varde: Number(k.avg_value) },
          jamfor: { etikett: "Riket", varde: Number(riket.avg_value), antal: 0 },
        };
      }
    }
    const kommun = benchmarks
      .filter(
        (x) =>
          x.scope === "kommunkod" &&
          x.scope_key === kommunkod &&
          x.skolform === m.skolform &&
          x.metric === m.metric &&
          x.avg_value != null &&
          Number(x.unit_count) >= JAMFOR_GOLV,
      )
      .sort((a, b) => b.time_period.localeCompare(a.time_period));
    for (const k of kommun) {
      const riket = benchmarks.find(
        (x) => x.scope === "national" && x.skolform === m.skolform && x.metric === m.metric && x.time_period === k.time_period && x.avg_value != null,
      );
      if (!riket) continue;
      return {
        matt: m,
        period: k.time_period,
        amne: { etikett: "Kommunen", varde: Number(k.avg_value) },
        jamfor: { etikett: "Riket", varde: Number(riket.avg_value), antal: Number(riket.unit_count) },
      };
    }
  }
  return null;
}
